import { Link } from "react-router-dom";
import { ArrowLeft, Rocket, Zap, Bug, Wrench, Sparkles } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Footer } from "@/components/Footer";

type ChangelogEntry = {
  date: string;
  version: string;
  tag: "launch" | "feature" | "fix" | "improvement";
  title: string;
  description: string;
  items: string[];
};

const tagConfig = {
  launch: { label: "Launch", icon: Rocket, className: "bg-accent/10 text-accent border-accent/20" },
  feature: { label: "Feature", icon: Sparkles, className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  fix: { label: "Fix", icon: Bug, className: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  improvement: { label: "Improvement", icon: Wrench, className: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" },
};

const changelog: ChangelogEntry[] = [
  {
    date: "March 11, 2026",
    version: "0.4.0",
    tag: "feature",
    title: "Command Palette & Keyboard Navigation",
    description: "Developer-native navigation with ⌘K command palette for instant access to any page or action.",
    items: [
      "⌘K / Ctrl+K command palette with fuzzy search",
      "Keyboard navigation across all pages",
      "Quick actions: theme toggle, navigation, integrations",
    ],
  },
  {
    date: "March 10, 2026",
    version: "0.3.0",
    tag: "feature",
    title: "Bug Fix Showcase with Visual Diffs",
    description: "Interactive showcase demonstrating how Healion identifies, diagnoses, and fixes real production bugs.",
    items: [
      "Step-by-step bug analysis walkthrough",
      "Visual diff between problem and solution",
      "Confidence scoring with detailed root cause analysis",
      "Dark mode optimized screenshots",
    ],
  },
  {
    date: "March 5, 2026",
    version: "0.2.0",
    tag: "feature",
    title: "Integration Pages & Platform Support",
    description: "Dedicated pages for each supported error monitoring tool and code platform.",
    items: [
      "Sentry, Datadog, New Relic, and Zipy integrations",
      "GitHub, GitLab, and Bitbucket platform pages",
      "Waitlist capture on each integration page",
    ],
  },
  {
    date: "February 28, 2026",
    version: "0.1.0",
    tag: "launch",
    title: "Healion AI Public Launch",
    description: "First public release of the Healion AI landing page with waitlist signup.",
    items: [
      "Hero section with terminal animation",
      "Problem/Solution sections highlighting manual vs automated bug fixing",
      "Email waitlist with Slack notifications",
      "Dark/light theme support",
      "Cost calculator for engineering time saved",
    ],
  },
];

const Changelog = () => {
  return (
    <main className="min-h-screen bg-background">
      <ThemeToggle />

      {/* Header */}
      <div className="border-b border-border">
        <div className="container mx-auto max-w-3xl px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Zap className="w-6 h-6 text-accent" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
              Changelog
            </h1>
          </div>
          <p className="text-muted-foreground">
            What's new in Healion AI — shipped fast, documented transparently.
          </p>
          <p className="text-xs text-muted-foreground/60 font-mono mt-2">
            Press <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px]">⌘K</kbd> to navigate anywhere
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border hidden sm:block" />

          <div className="space-y-12">
            {changelog.map((entry, index) => {
              const tag = tagConfig[entry.tag];
              const TagIcon = tag.icon;
              return (
                <div key={index} className="relative sm:pl-10">
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-accent bg-background hidden sm:block" />

                  {/* Date & version */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="font-mono text-xs text-muted-foreground">
                      {entry.date}
                    </span>
                    <span className="font-mono text-xs px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground">
                      v{entry.version}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${tag.className}`}>
                      <TagIcon className="w-3 h-3" />
                      {tag.label}
                    </span>
                  </div>

                  {/* Content */}
                  <h2 className="font-heading text-xl font-bold text-foreground mb-2">
                    {entry.title}
                  </h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    {entry.description}
                  </p>
                  <ul className="space-y-1.5">
                    {entry.items.map((item, i) => (
                      <li
                        key={i}
                        className="text-sm text-foreground/80 flex items-start gap-2"
                      >
                        <span className="text-accent mt-1 text-xs">▸</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
};

export default Changelog;
